import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { DeviceDto } from '../models/device.model';
import {
  DashboardFlightData,
  DashboardFlightDuration,
  DashboardFlightsBrief,
  DashboardReportErrors,
  DashboardServiceDoneItem,
  DashboardServiceDuePerDroneItem,
  Report,
  ReportQuantity,
  ReportRequest,
  ServiceDuePart,
  UAV,
} from '../models/report.model';
import { ReportService } from './report.service';

type ReportResult<T> = {
  data: T;
  error: unknown | null;
};

export type DashboardDataBuilderOptions = {
  includeServiceReports?: boolean;
};

const EMPTY_BRIEF: DashboardFlightsBrief = {
  totalFlightsCount: 0,
  totalFlightSeconds: 0,
  avgFleetFlightSeconds: 0,
};

const EMPTY_ERRORS: DashboardReportErrors = {
  brief: null,
  highestFlightDurations: null,
  lowestFlightDurations: null,
  serviceDuePerDrone: null,
  serviceDuePerPart: null,
  serviceDone: null,
};

const DASHBOARD_DATE_FILTER_PARAMETER_NAMES = new Set(['dateFrom', 'dateTo']);

@Injectable({
  providedIn: 'root',
})
export class DashboardDataBuilderService {
  constructor(private reportService: ReportService) {}

  getFlightsDashboardData(
    body: ReportRequest = {},
    options: DashboardDataBuilderOptions = {},
  ): Observable<DashboardFlightData> {
    const includeServiceReports = options.includeServiceReports ?? true;
    const serviceReportsBody = this.withoutParameters(
      body,
      DASHBOARD_DATE_FILTER_PARAMETER_NAMES,
    );

    return forkJoin({
      brief: this.mapReport(
        this.reportService.queryFlightsBriefReport(body),
        (report) => this.mapFlightsBrief(report),
        EMPTY_BRIEF,
      ),
      highestDuration: this.mapReport(
        this.reportService.queryFlightsDurationReport(
          this.withSortAscending(body, false),
        ),
        (report) => this.mapFlightDurations(report),
        [],
      ),
      lowestDuration: this.mapReport(
        this.reportService.queryFlightsDurationReport(
          this.withSortAscending(body, true),
        ),
        (report) => this.mapFlightDurations(report),
        [],
      ),
      serviceDuePerDrone: this.mapReport(
        includeServiceReports
          ? this.reportService.queryServiceDuePerDroneReport(serviceReportsBody)
          : of({ rows: [] }),
        (report) => this.mapServiceDueItems(report),
        [],
      ),
      serviceDuePerPart: this.mapReport(
        includeServiceReports
          ? this.reportService.queryServiceDuePerPartReport(serviceReportsBody)
          : of({ rows: [] }),
        (report) => this.mapServiceDueItems(report),
        [],
      ),
      serviceDone: this.mapReport(
        includeServiceReports
          ? this.reportService.queryServiceDoneReport(serviceReportsBody)
          : of({ rows: [] }),
        (report) => this.mapServiceDoneItems(report),
        [],
      ),
    }).pipe(
      map(
        ({
          brief,
          highestDuration,
          lowestDuration,
          serviceDuePerDrone,
          serviceDuePerPart,
          serviceDone,
        }) => ({
          brief: brief.data,
          flightDurations: highestDuration.data,
          lowestFlightDurations: lowestDuration.data,
          serviceDuePerDroneItems: serviceDuePerDrone.data,
          serviceDuePerPartItems: serviceDuePerPart.data,
          serviceDoneItems: serviceDone.data,
          errors: {
            ...EMPTY_ERRORS,
            brief: brief.error,
            highestFlightDurations: highestDuration.error,
            lowestFlightDurations: lowestDuration.error,
            serviceDuePerDrone: serviceDuePerDrone.error,
            serviceDuePerPart: serviceDuePerPart.error,
            serviceDone: serviceDone.error,
          },
        }),
      ),
    );
  }

  private withSortAscending(
    body: ReportRequest,
    sortAscending: boolean,
  ): ReportRequest {
    const parametersWithoutSort = (body.parameterValues ?? []).filter(
      (parameter) => parameter.name !== 'sortAscending',
    );

    return {
      ...body,
      parameterValues: [
        ...parametersWithoutSort,
        { name: 'sortAscending', value: sortAscending },
      ],
    };
  }

  private withoutParameters(
    body: ReportRequest,
    disallowedParameterNames: Set<string>,
  ): ReportRequest {
    const filteredParameters = (body.parameterValues ?? []).filter(
      (parameter) => !disallowedParameterNames.has(parameter.name ?? ''),
    );

    if (filteredParameters.length > 0) {
      return {
        ...body,
        parameterValues: filteredParameters,
      };
    }

    if ((body.parameterValues ?? []).length > 0) {
      const { parameterValues: _parameterValues, ...rest } = body;
      return rest;
    }

    return body;
  }

  private mapFlightsBrief(report: Report): DashboardFlightsBrief {
    const [totalFlightsCount, totalFlightSeconds, avgFleetFlightSeconds] =
      report.rows?.[0] ?? [];

    return {
      totalFlightsCount: this.toNumber(totalFlightsCount),
      totalFlightSeconds: this.toNumber(totalFlightSeconds),
      avgFleetFlightSeconds: this.toNumber(avgFleetFlightSeconds),
    };
  }

  private mapFlightDurations(report: Report): DashboardFlightDuration[] {
    const columns = report.descriptor?.columns ?? [];
    const uavIdColumnIndex = this.findColumnIndex(columns, 'uavId');
    const maxFlightTimeColumnIndex = this.findColumnIndex(
      columns,
      'maxFlightTime',
    );
    const totalFlightTimeColumnIndex = this.findColumnIndex(
      columns,
      'totalFlightTime',
    );
    const flightsCountColumnIndex = this.findColumnIndex(
      columns,
      'flightsCount',
    );

    return (report.rows ?? []).map((row) => {
      const safeRow = row ?? [];
      const fallbackTotalIndex = safeRow.length >= 4 ? 2 : 1;
      const fallbackCountIndex = safeRow.length >= 4 ? 3 : 2;
      const rawUav = this.getColumnValue(safeRow, uavIdColumnIndex, 0);
      const maxFlightTime = this.getColumnValue(
        safeRow,
        maxFlightTimeColumnIndex,
        1,
      );
      const totalFlightTime = this.getColumnValue(
        safeRow,
        totalFlightTimeColumnIndex,
        fallbackTotalIndex,
      );
      const flightsCount = this.getColumnValue(
        safeRow,
        flightsCountColumnIndex,
        fallbackCountIndex,
      );

      return {
        uav: this.parseUav(rawUav),
        // New schema returns both maxFlightTime and totalFlightTime; dashboard uses total.
        flightTime: this.toNumber(totalFlightTime ?? maxFlightTime),
        flightsCount: this.toNumber(flightsCount),
      };
    });
  }

  private mapServiceDueItems(
    report: Report,
  ): DashboardServiceDuePerDroneItem[] {
    const columns = report.descriptor?.columns ?? [];
    const uavIdColumnIndex = this.findColumnIndex(columns, 'uavId');
    const partIdColumnIndex = this.findColumnIndex(columns, 'partId');
    const metricTypeColumnIndex = this.findColumnIndex(columns, 'metricType');
    const usageRatioColumnIndex = this.findColumnIndex(columns, 'usageRatio');
    const valueColumnIndex = this.findColumnIndex(columns, 'value');
    const valueThresholdColumnIndex = this.findColumnIndex(
      columns,
      'valueThreshold',
    );

    return (report.rows ?? []).map((row) => {
      const safeRow = row ?? [];
      const usesLegacyDeviceAndUavColumns = safeRow.length >= 7;
      const rawDevice = this.getColumnValue(safeRow, uavIdColumnIndex, 0);
      const rawUav = this.getColumnValue(
        safeRow,
        uavIdColumnIndex,
        usesLegacyDeviceAndUavColumns ? 1 : 0,
      );
      const rawPartId = this.getColumnValue(
        safeRow,
        partIdColumnIndex,
        usesLegacyDeviceAndUavColumns ? 2 : 1,
      );
      const rawMetricType = this.getColumnValue(
        safeRow,
        metricTypeColumnIndex,
        usesLegacyDeviceAndUavColumns ? 3 : 2,
      );
      const rawUsageRatio = this.getColumnValue(
        safeRow,
        usageRatioColumnIndex,
        usesLegacyDeviceAndUavColumns ? 4 : 3,
      );
      const rawValue = this.getColumnValue(
        safeRow,
        valueColumnIndex,
        usesLegacyDeviceAndUavColumns ? 5 : 4,
      );
      const rawValueThreshold = this.getColumnValue(
        safeRow,
        valueThresholdColumnIndex,
        usesLegacyDeviceAndUavColumns ? 6 : 5,
      );

      const value = this.parseQuantity(rawValue);
      const threshold = this.parseQuantity(rawValueThreshold);
      const device = this.parseDevice(rawDevice);

      return {
        device:
          device.deviceId ||
          device.details ||
          device.deviceType ||
          device.deviceSummary
            ? device
            : this.parseDevice(rawUav),
        uav: this.parseUav(rawUav),
        partId: this.parsePart(rawPartId),
        metricType: typeof rawMetricType === 'string' ? rawMetricType : '',
        usageRatio: this.toNumber(rawUsageRatio),
        value: value.value,
        valueUnit: value.unit,
        valueThreshold: threshold.value,
        valueThresholdUnit: threshold.unit,
      };
    });
  }

  private mapServiceDoneItems(report: Report): DashboardServiceDoneItem[] {
    const columns = report.descriptor?.columns ?? [];
    const uavIdColumnIndex = this.findColumnIndex(columns, 'uavId');
    const partIdColumnIndex = this.findColumnIndex(columns, 'partId');
    const eventTypeColumnIndex =
      this.findFirstColumnIndex(columns, [
        'typeOfMaintenanceEvent',
        'maintenanceEventType',
        'eventType',
      ]) ?? this.findColumnIndex(columns, 'type');
    const maintainerColumnIndex = this.findColumnIndex(columns, 'maintainer');
    const timestampColumnIndex =
      this.findFirstColumnIndex(columns, ['timestamp', 'eventTimestamp']) ??
      this.findColumnIndex(columns, 'eventTime');

    return (report.rows ?? []).map((row) => {
      const safeRow = row ?? [];
      const usesLegacyDeviceAndUavColumns = safeRow.length >= 6;
      const rawDevice = this.getColumnValue(safeRow, uavIdColumnIndex, 0);
      const rawUav = this.getColumnValue(
        safeRow,
        uavIdColumnIndex,
        usesLegacyDeviceAndUavColumns ? 1 : 0,
      );
      const rawPartId = this.getColumnValue(
        safeRow,
        partIdColumnIndex,
        usesLegacyDeviceAndUavColumns ? 2 : 1,
      );
      const rawEventType = this.getColumnValue(
        safeRow,
        eventTypeColumnIndex,
        usesLegacyDeviceAndUavColumns ? 3 : 2,
      );
      const rawMaintainer = this.getColumnValue(
        safeRow,
        maintainerColumnIndex,
        usesLegacyDeviceAndUavColumns ? 4 : 3,
      );
      const rawTimestamp = this.getColumnValue(
        safeRow,
        timestampColumnIndex,
        usesLegacyDeviceAndUavColumns ? 5 : 4,
      );
      const device = this.parseDevice(rawDevice);

      return {
        device:
          device.deviceId ||
          device.details ||
          device.deviceType ||
          device.deviceSummary
            ? device
            : this.parseDevice(rawUav),
        uav: this.parseUav(rawUav),
        partId: this.parsePart(rawPartId),
        typeOfMaintenanceEvent: this.toText(rawEventType),
        maintainer: this.toText(rawMaintainer),
        timestamp: this.toText(rawTimestamp),
      };
    });
  }

  private mapReport<T>(
    report$: Observable<Report>,
    mapper: (report: Report) => T,
    fallbackData: T,
  ): Observable<ReportResult<T>> {
    return report$.pipe(
      map((report) => ({
        data: mapper(report),
        error: null,
      })),
      catchError((error: unknown) =>
        of({
          data: fallbackData,
          error,
        }),
      ),
    );
  }

  private parseUav(rawUav: unknown): UAV {
    const parsed = this.parseObject(rawUav);
    if (!parsed) {
      return {};
    }

    const details = this.parseObject(parsed['details']);
    const aircraftModel = this.parseObject(details?.['aircraftModel']);
    const deviceSummary = this.parseObject(parsed['deviceSummary']);
    const hardwareRevision = this.toFiniteNumber(
      parsed['hardwareRevision'] ??
        parsed['hardwarerevision'] ??
        parsed['hardware_revision'] ??
        details?.['hardwareRevision'] ??
        details?.['hardwarerevision'] ??
        details?.['hardware_revision'] ??
        aircraftModel?.['hardwareRevision'] ??
        aircraftModel?.['key'],
    );
    const unitId = this.toFiniteNumber(
      parsed['unitId'] ??
        parsed['unitid'] ??
        parsed['unit_id'] ??
        details?.['unitId'] ??
        details?.['unitid'] ??
        details?.['unit_id'],
    );
    const deviceId = this.toNonEmptyString(
      parsed['deviceId'] ?? parsed['deviceid'],
    );
    const modelName = this.toNonEmptyString(
      deviceSummary?.['modelName'] ?? aircraftModel?.['name'],
    );
    const nameAlias = this.toNonEmptyString(parsed['nameAlias']);
    const uav: UAV = {};

    if (hardwareRevision !== null) {
      uav.hardwareRevision = hardwareRevision;
    }

    if (unitId !== null) {
      uav.unitId = unitId;
    }

    if (deviceId) {
      uav.deviceId = deviceId;
    }

    if (modelName) {
      uav.modelName = modelName;
    }

    if (nameAlias) {
      uav.nameAlias = nameAlias;
    }

    return uav;
  }

  private findColumnIndex(
    columns: Array<{ title?: string }>,
    columnTitle: string,
  ): number | null {
    const normalizedColumnTitle = columnTitle.toLowerCase();
    const columnIndex = columns.findIndex(
      (column) => (column?.title ?? '').toLowerCase() === normalizedColumnTitle,
    );
    return columnIndex >= 0 ? columnIndex : null;
  }

  private getColumnValue(
    row: unknown[],
    columnIndex: number | null,
    fallbackIndex: number,
  ): unknown {
    if (columnIndex !== null) {
      return row[columnIndex];
    }

    return row[fallbackIndex];
  }

  private findFirstColumnIndex(
    columns: Array<{ title?: string }>,
    candidateTitles: string[],
  ): number | null {
    for (const title of candidateTitles) {
      const columnIndex = this.findColumnIndex(columns, title);
      if (columnIndex !== null) {
        return columnIndex;
      }
    }

    return null;
  }

  private toNumber(value: unknown): number {
    const numericCandidate = this.extractNumericValue(value);

    if (numericCandidate === undefined || numericCandidate === null) {
      return 0;
    }

    const parsed = Number(numericCandidate);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private parsePart(rawPart: unknown): ServiceDuePart {
    const parsed = this.parseObject(rawPart);
    if (!parsed) {
      return {};
    }

    const id = typeof parsed['id'] === 'string' ? parsed['id'] : undefined;
    const partType =
      typeof parsed['partType'] === 'string' ? parsed['partType'] : undefined;
    const partSubType =
      typeof parsed['partSubType'] === 'string'
        ? parsed['partSubType']
        : undefined;
    const part: ServiceDuePart = {};

    if (id) {
      part.id = id;
    }

    if (partType) {
      part.partType = partType;
    }

    if (partSubType) {
      part.partSubType = partSubType;
    }

    return part;
  }

  private parseQuantity(value: unknown): { value: number; unit: string } {
    const numericValue = this.toNumber(value);
    const unit = this.extractUnit(value);

    return { value: numericValue, unit };
  }

  private extractNumericValue(value: unknown): unknown {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'value' in value
    ) {
      return (value as ReportQuantity).value;
    }

    return value;
  }

  private extractUnit(value: unknown): string {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'unit' in value &&
      typeof (value as ReportQuantity).unit === 'string'
    ) {
      return (value as ReportQuantity).unit ?? '';
    }

    return '';
  }

  private toText(value: unknown): string {
    if (value === undefined || value === null) {
      return '';
    }

    return typeof value === 'string' ? value : String(value);
  }

  private parseDevice(rawDevice: unknown): DeviceDto {
    const parsed = this.parseObject(rawDevice);
    if (!parsed) {
      return {};
    }

    return parsed as DeviceDto;
  }

  private toNonEmptyString(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    return value.trim().length > 0 ? value : undefined;
  }

  private parseObject(value: unknown): Record<string, unknown> | null {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }

    if (typeof value !== 'string') {
      return null;
    }

    try {
      const parsed = JSON.parse(value) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }

    return null;
  }

  private toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }
}
